<template>
  <form @submit.prevent="saveNewActivity">
    <div class="layout-issue">
      <HeaderBar
        v-if="!issueId"
        :modifiers="['transparent', 'more-height']"
      >
        {{ $t('issues.new_issue') }}
        <template #right>
          <router-link
            class="button"
            :to="{
              name: isShaftV2 ? 'shaftV2Inspection' : 'shaftInspection',
              params: { scope: 'inspections' },
            }"
            @click.passive="shaftIssueLocationStore.$reset()"
          >
            {{ $t('general.cancel') }}
          </router-link>
          <FormSubmitButton :isSaving="saving" />
        </template>
      </HeaderBar>
      <IssueNav
        v-if="issueId && initialActivity"
        :inspectionId="inspectionId"
        :issue="initialActivity"
        :navRouterPath="setNavRouterPath"
        :useRouterPath="setRouterPathStatus"
      />
      <div
        v-if="!issueId"
        class="issue__section issue__section--lighter issue__section--bar"
      >
        <div class="issue__checkbox">
          <Checkbox
            id="new_issue"
            v-model="shaftIssueLocationStore.saveAndCreateNewIssue"
            :modifiers="['switch']"
          >
            <p class="typo-small-title">
              {{ $t('issues.new_issue_after_saving') }}
            </p>
          </Checkbox>
        </div>
      </div>

      <div class="issue">
        <div class="issue-details scroll-container">
          <div
            v-if="!loading"
            class="issue__body"
          >
            <div
              v-if="isShaftV2"
              class="issue__title typo-title"
            >
              <IssueEditField
                ref="textTitle"
                :canEdit="canEdit"
                :error="hasError('title')"
                field="title"
                :issue="activity"
                :showLabel="false"
                @input="validateInput('title')"
                @update="loadIssue"
              />
            </div>

            <div
              v-if="!isShaftV2"
              class="issue__section typo-small-title"
            >
              <IssueLocationInput
                v-model="location"
                :canEdit="canEdit"
                :error="hasError('member')"
                :placeholder="$t('shafts.issues.select_a_member')"
                @update:model-value="updateLocation"
              />
            </div>

            <!-- Set location stuff ShaftV2 -->
            <div
              v-if="isShaftV2"
              class="issue__location"
            >
              <router-link
                :class="{
                  'issue__thumbnail': true,
                  'disabled': disableLocation
                }"
                :to="shaftData.location"
              >
                <MapPreview
                  v-if="!location"
                  :emptyText="emptyText"
                />
                <template v-else>
                  <IssueLocationPreview
                    v-if="location.type === 'shaft'"
                    :currentPoint="location"
                    :organisationId="organisationId"
                    :shaftId="shaftId"
                  />
                  <div
                    v-else
                    class="layout-regions__map-wrapper"
                  >
                    <div
                      class="
                        layout-regions__map
                        show-for-medium
                        layout-regions__map--center
                      "
                    >
                      <LeafletMap
                        ref="map"
                        :diagram="location.diagram_identifier"
                        :dragging="false"
                        :zooming="false"
                        @init="onMapInit(location)"
                      />
                      <div class="layout-regions__overlay" />
                    </div>
                  </div>
                </template>
              </router-link>
              <div class="issue__content">
                <p :class="{ error: hasError('location') }">
                  <span v-if="!location">{{ $t('issues.set_issue_location') }}</span>
                  <span v-else>{{ $t('issues.issue_location') }}</span>
                </p>
                <div
                  :class="{
                    'issue__input issue__input--placeholder typo-ignore': true,
                    'issue__input--margin-top': location,
                  }"
                >
                  <p
                    v-if="!location"
                    class="issue__input--margin-top"
                  >
                    {{ $t('issues.no_issue_location') }}
                  </p>
                  <div v-else>
                    <p v-if="locationText.location">
                      {{ locationText.location }}
                    </p>
                    <p v-if="locationText.elementType">
                      {{ $t('shafts.issues.element_type') }}:
                      {{ locationText.elementType }}
                    </p>
                    <p v-if="locationText.element">
                      {{ $t('shafts.issues.element') }}:
                      {{ locationText.element }}
                    </p>
                    <p v-if="locationText.region">
                      {{ locationText.region }}
                    </p>
                  </div>
                </div>
                <router-link
                  :class="{
                    'button button--small button--full-width-mobile typo-button issue__input--margin-top': true,
                    'button--disabled': disableLocation
                  }"
                  :to="shaftData.location"
                >
                  {{ !!location ? $t('issues.change_location') : $t('issues.set_location') }}
                </router-link>
              </div>
            </div>

            <div
              :class="{
                'issue__stack-group': true,
                'issue__stack-group--reverse': isShaftV2,
              }"
            >
              <div class="issue__text-group">
                <div class="issue__section typo-small-title">
                  <IssueEditField
                    ref="textDescription"
                    :canEdit="canEdit"
                    :error="hasError('detail.description') || hasError('description')"
                    field="description"
                    :issue="isShaftV2 ? activity : activity.detail"
                    :placeholder="$t('issues.no_description')"
                    @input="validateInput('detail.description') || validateInput('description')"
                    @update="loadIssue"
                  />
                  <IssueEditField
                    v-if="isShaftV2"
                    ref="textRecommendation"
                    :canEdit="canEdit"
                    :error="hasError('recommendation')"
                    field="recommendation"
                    :issue="activity"
                    :placeholder="$t('issues.no_recommendation')"
                    @input="validateInput('recommendation')"
                    @update="loadIssue"
                  />
                </div>
              </div>
              <!-- Image upload Shaft -->
              <div
                :class="{
                  'issue__section issue__section-image issue__section--light issue__section--horizontal-fade': true,
                  'issue__section--margin-top': isShaftV2,
                  'issue__section--margin-top-small': !isShaftV2,
                  'issue__image--error': !imageValid,
                }"
              >
                <div class="issue-field">
                  <div class="issue-field__wrapper">
                    <FileUploader
                      v-if="canEdit"
                      :acceptedFileTypes="acceptedFileTypes"
                      fileType="image"
                      :label="$t('issues.add_image')"
                      :modifiers="['always-show']"
                      @update-blob="saveBlob($event, 'image')"
                      @update-error="wrongFileType"
                      @update-reader="updateFileList($event, 'image')"
                    />
                    <div class="issue-field__header">
                      <div class="issue-field__title typo-small-title">
                        {{
                          pluralize(
                            activity.detail.issue_images.length,
                            $t('issues.image'),
                            $t('issues.images')
                          )
                        }}
                      </div>
                    </div>
                    <div
                      :class="{
                        'issue-field__body': true,
                        'issue-field__body--padding-bottom': sortedImages?.length > 0
                      }"
                    >
                      <div
                        ref="imagesContainer"
                        class="issue__images"
                      />
                      <teleport to="body">
                        <ImageViewer
                          v-model:currentImage="viewingImage"
                          :currentOperation="currentShaft"
                          :images="sortedImages"
                          :issue="activity"
                          :issuePath="shaftData.issueUrl"
                          @annotate-image="annotateImage"
                          @apply-damage-rating="applyDamageRating"
                          @clear-damage-rating="clearDamageRating"
                          @update-image="updateCurrentImage"
                        >
                          <p class="typo-ignore typo-title">
                            <strong>{{ locationDescription }}</strong>
                          </p>
                          <p class="typo-ignore typo-tiny">
                            {{ $t('inspections.issue') }}: #{{ shortId }}
                          </p>
                        </ImageViewer>
                      </teleport>
                      <template
                        v-for="image in sortedImages"
                        :key="image.id"
                      >
                        <teleport
                          v-if="image.htmlElement"
                          :to="image.htmlElement"
                        >
                          <div
                            v-if="canEdit"
                            class="issue__image-menu"
                          >
                            <UseSvg
                              class="icon"
                              name="vertical_ellipsis"
                            />
                            <div
                              class="issue__image-menu-item"
                              @click="confirmDeleteDialog('image', image.id)"
                            >
                              <div class="issue__image-menu-item-content">
                                <UseSvg
                                  class="icon"
                                  name="trash_16"
                                />
                                {{ $t('issues.remove_image') }}
                              </div>
                            </div>
                          </div>
                        </teleport>
                      </template>
                      <p
                        v-if="activity.detail.issue_images.length === 0"
                        class="typo-small-title"
                      >
                        {{ $t('issues.no_images') }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <FormStandAloneErrorMessage
                v-if="!imageValid && !isShaftV2"
                :errorMessage="imageValidationMessage"
              />
            </div>

            <div
              v-if="isShaftV2"
              class="issue__section issue__section--light"
            >
              <div class="issue__checkbox">
                <Checkbox
                  id="requires_task"
                  v-model="activity.requires_task"
                  :disabled="!canEdit"
                  :modifiers="['switch']"
                  @update:model-value="update('requires_task', $event)"
                  @update="loadIssue"
                >
                  <p class="typo-small-title">
                    {{ $t('issues.requires_tasks') }}
                  </p>
                </Checkbox>
              </div>
            </div>

            <div
              v-if="activity.requires_task"
              class="issue__tasks issue__section issue__section--light"
            >
              <div class="issue__tasks-header">
                <div class="issue-field">
                  <div class="issue-field__wrapper">
                    <div class="issue-field__header">
                      <div class="issue-field__title typo-small-title">
                        {{
                          pluralize(
                            activity.tasks.length,
                            $t('issues.task'),
                            $t('issues.tasks')
                          )
                        }}
                      </div>
                    </div>

                    <div
                      :class="{
                        'issue-field__body': true,
                        'issue-field__body--persist':
                          activity.tasks.length === 0,
                      }"
                    >
                      <button
                        v-show="activity?.policy?.create"
                        :aria-label="$t('issues.add_task')"
                        class="issue__tasks-new"
                        type="button"
                        @click="createNewTask"
                      >
                        <UseSvg
                          class="icon icon--small"
                          name="plus_16"
                        />
                      </button>

                      <IssueTaskFormModal
                        v-if="newTask"
                        :errorSaving="errorSavingTask"
                        :initialTask="newTask"
                        :inspectionDate="initialActivity.inspection.date"
                        :issue="activity"
                        @cancel="newTask = null"
                        @submit="addNewTask"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div class="issue__tasks-content">
                <IssueTask
                  v-for="task in activity.tasks.sort(byCreatedAt)"
                  :key="task.id"
                  :canEdit="!canEdit"
                  :errorSaving="errorSavingTask"
                  :issue="activity"
                  :open="toggleIssueModal"
                  :task="task"
                  useServiceToClose
                  @delete="confirmDeleteDialog('task', task.id)"
                  @mark-as="markTaskAs(task, $event)"
                  @update="updateTask(task, $event)"
                />
                <div
                  v-if="activity.tasks.length === 0"
                  class="
                    issue__tasks-content-empty issue__section--last
                    typo-small-text
                  "
                >
                  {{ $t('empty_states.tasks_empty') }}
                </div>
              </div>
              <div
                v-if="activity.tasks.length !== 0"
                class="issue__section issue__section--last"
              >
                <div class="issue__checkbox--resolve">
                  <Checkbox
                    id="tasks_close_issue"
                    v-model="activity.tasks_close_issue"
                    :disabled="!canEdit"
                    @update:model-value="update('tasks_close_issue', $event)"
                  >
                    <div class="issue__checkbox--info">
                      <p class="typo-field-text">
                        {{ $t('tasks.completing_tasks') }}
                      </p>
                      <p class="typo-ignore typo-tiny">
                        {{ $t('tasks.marked_as_resolved') }}
                      </p>
                    </div>
                  </Checkbox>
                </div>
              </div>
            </div>

            <!-- Document upload -->
            <div class="issue__section issue__section-document issue__section--light">
              <AttachmentUploader
                :canEdit="canEdit"
                v-model:documentList="activity.detail.issue_documents"
                @update-blob="saveBlob($event, 'attachment')"
                @delete-file="removeDocument"
              />
            </div>
          </div>
          <Loader v-if="loading" />
        </div>

        <div class="issue-ratings typo-small-title">
          <template v-if="!loading">
            <IssueRiskRating
              v-if="showRisk && issueId && isShaftV2"
              :issue="activity"
            />
            <!-- Status -->
            <div
              v-if="issueId && isShaftV2"
              class="issue-inline-field typo-small-title"
            >
              <div class="issue-inline-field__title">
                {{ $t('issues.status') }}
              </div>
              <div class="issue-inline-field__field">
                <Select
                  v-model="activity.status"
                  :disabled="!canEdit"
                  :items="statusOptions"
                  labelStyle="pill"
                  @update:model-value="update('status', $event)"
                />
              </div>
            </div>
            <!-- Type/Material -->
            <div
              v-if="isShaftV2"
              :class="{
                'issue-inline-field typo-small-title': true,
                'issue-inline-field--error': hasError('element_material'),
              }"
            >
              <div class="issue-inline-field__title">
                {{ $t('issues.material') }}
              </div>
              <div class="issue-inline-field__field">
                <Select
                  :disabled="!canEdit"
                  :items="elementMaterialOptions"
                  :modelValue="activity.element_material"
                  :placeholder="$t('issues.unspecified')"
                  @update:model-value="updateElementMaterial"
                />
              </div>
            </div>

            <!-- Element Class -->
            <div
              v-if="isShaftV2"
              :class="{
                'issue-inline-field typo-small-title': true,
                'issue-inline-field--error': hasError('element_class'),
              }"
            >
              <div class="issue-inline-field__title">
                {{ $t('issues.class') }}
              </div>
              <div class="issue-inline-field__field">
                <DescriptionSelect
                  v-model="activity.element_class"
                  :items="elementClassOptions"
                  :label="$t('issues.class')"
                  name="element_class"
                  :placeholder="$t('issues.unspecified')"
                  :removable="false"
                  selectClasses="issue-ratings__dropdown"
                  :simple="true"
                  @update:model-value="update('element_class', $event)"
                />
              </div>
            </div>
            <!-- Condition rating -->
            <div
              v-if="!isShaftV2"
              class="
                issue-inline-field issue-inline-field--offset-field
                typo-small-title
              "
            >
              <div class="issue-inline-field__title">
                {{ $t('shafts.issues.condition_rating') }}
              </div>
              <div class="issue-inline-field__field">
                <div
                  v-if="activity.overall_condition_rating"
                  :class="`issue-ratings__risk issue-ratings__condition--${activity.overall_condition_rating}`"
                >
                  <span>{{ $t(`condition_percentages.${activity.overall_condition_rating}`) }}</span>
                </div>
              </div>
            </div>

            <div class="issue-ratings__section">
              <div class="issue-inline-field issue-inline-field--heading">
                <div class="issue-inline-field__title">
                  {{ $t('issues.deterioration_mechanisms') }}
                </div>
                <div class="issue-inline-field__field">
                  <div
                    v-if="canEdit && Object.keys(deteriorationMechanisms).length > 0"
                    class="select select--input issue-ratings__dropdown"
                  >
                    <div class="dropdown">
                      <Popper
                        v-model:open="newRatingOpen"
                        placement="bottom-end"
                      >
                        <template #trigger>
                          <div class="dropdown__trigger">
                            <div class=" select__trigger issue-ratings__drop-trigger">
                              <button
                                :aria-label="$t('issues.add_deterioration_mechanism_rating')"
                                class="select__label select__label--flex"
                                type="button"
                              >
                                <Plus16Svg />
                              </button>

                              <span class="select__arrow">
                                <UseSvg
                                  class="icon icon--small"
                                  name="arrow_down_16"
                                />
                              </span>
                            </div>
                          </div>
                        </template>

                        <div
                          v-if="!newRating"
                          class="menu menu--dropdown"
                        >
                          <ul class="menu__items">
                            <li
                              v-for="(detMech, name) in deteriorationMechanisms"
                              :key="name"
                              class="menu-item menu-item--dropdown"
                            >
                              <button
                                class="menu-item__link"
                                type="button"
                                @click="createNewRating(name, detMech)"
                              >
                                <span class="menu-item__label">
                                  {{ $t(`deterioration_mechanisms.${name}.name`) }}
                                </span>
                              </button>
                            </li>
                          </ul>
                        </div>

                        <DescriptionSelectDropdown
                          v-if="newRating"
                          :addText="$t('issues.add_rating')"
                          iconName="rating_circle"
                          :items="ratingItems(newRating)"
                          :label="$t(`deterioration_mechanisms.${newRating.name}.name`)"
                          :modelValue="existingDetMechs[newRating.name]"
                          name="new_rating"
                          :ratingScale="true"
                          :removable="false"
                          @cancel="cancelNewRating"
                          @update:model-value="addRating"
                        />
                      </Popper>
                    </div>
                  </div>
                </div>
              </div>

              <div class="issue__ratings-container">
                <div
                  v-for="rating in activity.deterioration_mechanism_ratings"
                  :key="rating.id"
                  class="issue-inline-field"
                >
                  <div class="issue-inline-field__title">
                    <a
                      v-if="hasAcceptedImage(rating)"
                      class="issue-inline-field__calculated-icon"
                      @click.prevent="openDamageCalculation(acceptedImage.id)"
                    >
                      <WrenchIcon />
                    </a>
                    {{ $t(`deterioration_mechanisms.${rating.name}.name`) }}
                  </div>
                  <div class="issue-inline-field__field">
                    <DescriptionSelect
                      iconName="rating_circle"
                      :items="ratingItems(rating)"
                      :label="$t(`deterioration_mechanisms.${rating.name}.name`)"
                      :modelValue="rating.value"
                      :name="rating.name"
                      :ratingScale="true"
                      :selectIcon="true"
                      selectClasses="issue-ratings__dropdown"
                      @change="updateDescription(rating)"
                      @remove="removeRating(rating)"
                      @update:model-value="updateRating(rating, $event)"
                    />
                  </div>
                </div>
                <div
                  v-if="activity.deterioration_mechanism_ratings.length === 0"
                  class="issue-inline-field issue-inline-field--empty"
                >
                  <div class="issue-inline-field__icon">
                    <ExclamationTriangleSvg />
                  </div>
                  <div class="issue-inline-field__title">
                    {{ $t('issues.none_specified') }}
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="isShaftV2"
              class="issue-ratings__section"
            >
              <div class="issue-inline-field issue-inline-field--heading">
                <div class="issue-inline-field__title">
                  {{ $t('issues.risk_factors') }}
                </div>
              </div>
              <div
                :class="{
                  'issue-inline-field typo-small-title': true,
                  'issue-inline-field--error': hasError(
                    'overall_condition_rating'
                  ),
                }"
              >
                <div class="issue-inline-field__title">
                  {{ $t('issues.overall_condition') }}
                </div>
                <div class="issue-inline-field__field">
                  <DescriptionSelect
                    iconName="rating_square"
                    :items="overallConditionOptions"
                    :label="$t('issues.overall_condition')"
                    :modelValue="activity.overall_condition_rating"
                    name="overall_condition_rating"
                    placeholder="-"
                    :ratingScale="true"
                    :removable="false"
                    :selectIcon="true"
                    selectClasses="issue-ratings__dropdown"
                    @update:model-value="update('overall_condition_rating', $event)"
                  />
                </div>
              </div>
              <div
                :class="{
                  'issue-inline-field typo-small-title': true,
                  'issue-inline-field--error': hasError('priority'),
                }"
              >
                <div class="issue-inline-field__title">
                  {{ $t('issues.repair_priority') }}
                </div>
                <div class="issue-inline-field__field">
                  <DescriptionSelect
                    iconName="rating_triangle"
                    :items="repairPriorityOptions"
                    :label="$t('issues.repair_priority')"
                    :modelValue="activity.priority"
                    name="priority"
                    placeholder="-"
                    :ratingScale="true"
                    :removable="false"
                    :selectIcon="true"
                    selectClasses="issue-ratings__dropdown"
                    @update:model-value="update('priority', $event)"
                  />
                </div>
              </div>
            </div>

            <div
              v-if="issueId"
              class="issue-ratings__section"
            >
              <div class="issue-inline-field issue-inline-field--heading">
                <div class="issue-inline-field__title">
                  {{ $t('issues.details') }}
                </div>
              </div>

              <div class="form__group-inner">

                <!-- Issue Number -->
                <FormGroup
                  :label="$t('issues.issue_number')"
                  :value="shortId"
                  :modifiers="['narrow', 'id']"
                />

                <!-- Reporter -->
                <FormGroup
                  :label="$t('issues.reporter')"
                  :value="activity.created_by.full_name"
                  :modifiers="['narrow']"
                />

                <!-- Captured At -->
                <FormGroup
                  :label="$t('issues.captured')"
                  :value="activity?.created_at ? formatDate(activity.created_at) : ''"
                  :modifiers="['narrow']"
                />

                <div
                  v-if="issueId && downloadIssuePath"
                  class="issue__download issue__download--no-margin-bottom issue__download--no-border"
                >
                  <a
                    class="button button--inline button--full-width"
                    :href="downloadIssuePath"
                    target="_blank"
                  >
                    {{ $t('general.download') }}
                  </a>
                </div>
              </div>
            </div>

            <!-- History -->
            <div
              v-if="issueId && activity.total_modifications_count"
              class="issue-ratings__section"
            >
              <div class="issue-inline-field issue-inline-field--heading">
                <div class="issue-inline-field__title">
                  {{
                    pluralize(
                      activity.total_modifications_count,
                      $t('issues.modification'),
                      $t('issues.modifications')
                    )
                  }}
                </div>

                <div
                  class="issue-inline-field__field"
                >
                  <router-link
                    class="button button-small"
                    :to="{ name: shaftData.history }"
                  >
                    {{ $t('issues.view_history') }}
                  </router-link>
                </div>
              </div>
            </div>
            <div
              v-if="!issueId && isShaftV2"
              class="issue-ratings__section issue-ratings__section--large-gap"
            >
              <IssueRiskRating v-if="showRisk" :issue="activity" />
            </div>
          </template>
          <div
            v-if="issueId && activity?.policy?.destroy"
            class="issue__delete"
          >
            <button
              class="button button--inline button--full-width button--danger"
              type="button"
              @click="setDialogTypeAndId('issue', issueId)"
            >
              {{ $t('general.delete_issue') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </form>
  <Dialog
    v-if="dialogIsOpen"
    :message="dialogData.text.message"
    :title="dialogData.text.title"
    @dismiss-dialog="dismissDialogAction"
  >
    <template #actions>
      <button
        aria-label="delete"
        class="button button--inverse"
        type="button"
        @click="dialogData.action"
      >
        {{ $t('general.yes') }}
      </button>
    </template>
  </Dialog>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount, nextTick, watch, inject, watchEffect } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useShaftV2, useShaft } from '@/functions/use_remote_object';
import I18n from 'i18n-js';
import { v4 as uuid } from 'uuid';

import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import { acceptedFileTypes } from '@/config/accepted_file_types';
import useUploader from '@/vendor/use_uploader';
import useAjax from '@/functions/use_ajax';
import useEmitter from '@/functions/use_emitter';
import pluralize from '@/functions/pluralize';
import { byCreatedAt } from '@/functions/order_by';
import sliceObject from '@/functions/slice_object';
import { renderShortId } from '@/functions/utils';
import {
  SHAFT_DETERIORATION_MECHANISMS,
  STATION_DETERIORATION_MECHANISMS,
  DEFAULT_RATINGS,
} from '@/functions/simm/shafts/deterioration_and_maintenance';
import DETERIORATION_MECHANISMS from '@/functions/simm/deterioriation_mechanisms';
import { isoDate, formatDate } from '@/functions/date_format_helpers';
import ratingToPriority from '@/functions/simm/rating_to_priority';

import { useIssueLocationStore } from '@/stores/shafts/issueLocationStore';
import { useIssuesStore } from '@/stores/issues/issuesStore';
import useOrganisationProfile from '@/functions/use_organisation_profile';

import HeaderBar from '@/components/generic/header_bar.vue';
import Loader from '@/components/generic/loader.vue';
import UseSvg from '@/components/generic/use_svg.vue';
import Popper from '@/components/generic/popper.vue';
import FormSubmitButton from '@/components/form/form_submit_button.vue';
import Checkbox from '@/components/form/checkbox.vue';
import DescriptionSelectDropdown from '@/components/form/description_select_dropdown.vue';
import DescriptionSelect from '@/components/form/description_select.vue';
import Select from '@/components/form/select.vue';

import IssueNav from '@/components/issues/issue_nav.vue';
import IssueEditField from '@/components/issues/issue_edit_field.vue';
import IssueLocationInput from '@/components/shafts/issue_location_input.vue';
import IssueCarousel from '@/components/issues/issue_carousel';
import IssueRiskRating from '@/components/issues/issue_risk_rating.vue';
import IssueTask from '@/components/issues/issue_task.vue';
import IssueTaskFormModal from '@/components/issues/issue_task_form_modal.vue';
import Dialog from '@/components/generic/dialog.vue';
import ImageViewer from '@/components/image_viewer.vue';
import FileUploader from '@/components/generic/file_uploader.vue';
import AttachmentUploader from '@/components/generic/attachment_uploader.vue';
import IssueLocationPreview from '@/components/shaftsV2/location/issue_location_preview.vue';
import FormStandAloneErrorMessage from '@/components/form/form_stand_alone_error_message.vue';
import FormGroup from '@/components/form/form_group.vue';

import Plus16Svg from '@/images/plus-16.svg';
import ExclamationTriangleSvg from '@/images/exclamation-triangle.svg';
import WrenchIcon from '@/images/wrench-icon.svg'
import MapPreview from '@/components/generic/map_preview.vue';

import LeafletMap from '@/vendor/components/leaflet_map.vue';
import {
  rgeoToLatLng,
  geoJSONPolygonToLatLngs,
  rgeoPolygonToGeoJSON,
} from '@/functions/geospatial_conversions';
import regionPolygonOptions from '@/functions/region_polygon_options';
import { screenAtLeast } from '@/functions/screen_width';

import { DotIcon } from '@/vendor/leaflet_mods';

const PointIcon = DotIcon.extend({
  options: {
    className: 'map-modal__point',
    iconSize: [9, 9],
    iconAnchor: [5, 5],
  },
});
const shaftIssueLocationStore = useIssueLocationStore();

const props = defineProps({
  organisationId: { type: String, required: true },
  shaftId: { type: String, required: true },
  inspectionId: { type: String, required: true },
  issueId: { type: String, default: null },
  setGroupId: { type: String, default: null },
  setNumber: { type: String, required: false, default: null },
  /** Use a specific route for the header nav back button */
  issueNavBackRoute: { type: String, required: false, default: null },
});

const issueStore = useIssuesStore();

const { showRisk, isAga } = useOrganisationProfile();

const route = useRoute();
const router = useRouter();
const isShaftV2 = computed(() => route.path.includes('shafts_v2'));
const currentOrganisation = inject('currentOrganisation');
const currentShaft = isShaftV2.value ? useShaftV2(props) : useShaft(props);
const uploader = useUploader();
const ajax = useAjax();
const emitter = useEmitter();

let lastGoodActivity;
const currentUser = inject('currentUser');
const saving = ref(false);
const loading = ref(true);
const initialActivity = ref(null);
const activity = reactive(createNewActivity());
const location = ref(null);
const shortId = computed(() => props.issueId ? renderShortId(props.issueId) : '');
const errors = ref(null);
const initialInspectionDate = ref(null);
const existingDetMechs = ref({});
const existingRating = ref(0);

const newRatingOpen = ref(false);
const newRating = ref(null);
const newTask = ref(null);
const issueName = ref(null);

const imagesContainer = ref(null);
const viewingImage = ref(null);

const imageValid = ref(true);
const imageValidationMessage = ref(null);

const errorSavingTask = ref(null);
const toggleIssueModal = ref(null);
const dialogIsOpen = ref(false);
const deletionId = ref(null);
const currentDialogType = ref(null);
const textTitle = ref();
const textDescription = ref();
const textRecommendation = ref();
const issueEditFieldInputs = [
  textTitle,
  textDescription,
  textRecommendation
];

const loadingOperation = ref(true);
const loadingOperationRegion = ref(true);
const operationRegions = ref(null);
const map = ref(null);
let marker = null;

const emptyText = computed(() => {
  return I18n.t('issues.no_location');
});

const sortedImages = computed(() => {
  return activity.detail.issue_images.filter((x) => x.attached).sort(byCreatedAt);
});
const locationText = computed(() => {
  if (!location.value) return;
  let operation = '';
  switch (location.value?.type) {
    case 'shaft':
      return {
        location: `${I18n.t('shafts.issues.set')} ${location.value.set}`,
        element: location.value.element.name,
        elementType: I18n.t(`shafts.element_types.type_singular.${location.value.elementType}`)
      };
    case 'station':
    case 'heavy_mobile_equipment':
      operation = location.value[location.value?.type];
      return {
        location: `${I18n.t('shafts.issues.' + location.value?.type)}: ${operation?.name}`,
        region: `${I18n.t('operations.region')}: ${operation?.region || I18n.t('general.none')}`
      };
    default:
      return null;
  }
});

const shaftData = computed(() => {
  if (isShaftV2.value) {
    const baseUrl = `/organisations/${props.organisationId}/shafts_v2/shafts/${props.shaftId}`;
    const inspectionUrl = `${baseUrl}/inspections/${props.inspectionId}`;
    const locationValue = {
        params: {
          organisationId: props.organisationId,
          shaftId: props.shaftId,
          inspectionId: props.inspectionId,
          issueId: props.issueId,
        },
      };

    if (location.value?.type === 'station') {
      const stationId = location.value?.station?.station_id;
      !hasIssueNavBackRoute.value && (locationValue.name = stationId ? 'shaftV2IssueLocationStation' : 'shaftV2IssueLocationStations');
      hasIssueNavBackRoute.value && (locationValue.name = 'shaftV2IssuesLocationTypeStation');
      stationId && (locationValue.params.stationId = stationId);
    } else if (location.value?.type === 'heavy_mobile_equipment') {
      if (currentShaft.value?.policy?.heavy_mobile_equipment_enabled) {
        const hmeId = location.value?.heavy_mobile_equipment?.heavy_mobile_equipment_id;
        !hasIssueNavBackRoute.value && (locationValue.name = hmeId ? 'shaftV2IssueLocationHme' : 'shaftV2IssueLocationHmes');
        hasIssueNavBackRoute.value && (locationValue.name = 'shaftV2IssuesLocationTypeHme');
        hmeId && (locationValue.params.heavyMobileEquipmentId = hmeId);
      } else {
        locationValue.name = 'shaftV2Issue';
      }
    } else {
      const set = location.value?.set;
      if (!hasIssueNavBackRoute.value) {
        locationValue.name = set ? 'shaftV2IssueLocationSet' : 'shaftV2NewIssueLocationSets';
      } else {
        const isOverview = route.fullPath.includes('overview');
        const overviewPage = route.params.setGroupId ? 'shaftV2IssuesLocationTypeOverviewId' : 'shaftV2IssuesLocationTypeOverview';
        const pageName = isOverview ? overviewPage : 'shaftV2IssuesLocationTypeSet';
        locationValue.name = pageName;
        set && (locationValue.params.setNumber = set);
      }

      set && (locationValue.params.setId = set);
    }
    return {
      name:'shafts_v2',
      inspection: 'shaftV2Inspection',
      inspectionUrl: inspectionUrl,
      history: 'shaftV2IssueHistory',
      location: locationValue,
      issuesUrl: `${inspectionUrl}/issues`,
      issueUrl: `${inspectionUrl}/issues/${props.issueId}`,
      setElements: `${baseUrl}/sets`,
      stationLocationUrl: `${baseUrl}/stations`,
      stationRegions: 'station_regions',
      hmeLocationUrl: `${baseUrl}/heavy_mobile_equipments`,
      hmeRegions: 'heavy_mobile_equipment_regions',
    };
  } else {
    const baseUrl = `/organisations/${props.organisationId}/shafts/${props.shaftId}`;
    const inspectionUrl = `${baseUrl}/inspections/${props.inspectionId}`;
    return {
      name:'shafts',
      inspection: 'shaftInspection',
      inspectionUrl: inspectionUrl,
      history: 'shaftIssueHistory',
      location: '',
      shaftLocationUrl: `${baseUrl}/shaft_elements`,
      stationLocationUrl: `${baseUrl}/stations`,
      stationRegions: 'station_regions',
      hmeLocationUrl: `${baseUrl}/heavy_mobile_equipments`,
      hmeRegions: 'heavy_mobile_equipment_regions',
      issuesUrl: `${inspectionUrl}/issues`,
      issueUrl: `${inspectionUrl}/issues/${props.issueId}`
    };
  }
});

/**
 * Finds the accepted det mech image
 */
const acceptedImage = computed(() => {
  return activity.detail.issue_images.find((image) => {
    return image.accepted === true;
  });
});

// Checks if a specific route was passed in via props
const hasIssueNavBackRoute = computed(() => {
  return props.issueNavBackRoute !== null;
});

// Default back route - to inspections
const defaultNavBackRoute = computed(() => {
  return {
    name: shaftData.value.inspection,
    params: { inspectionId: props.inspectionId, scope: 'inspections' },
  };
});

// Set back route based on props
const setNavRouterPath = computed(() => {
  return hasIssueNavBackRoute.value ? { name: props.issueNavBackRoute } : defaultNavBackRoute.value;
});

// Set route path status based on props and history
const setRouterPathStatus = computed(() => {
  return hasIssueNavBackRoute.value || useRouterPath(router?.options?.history?.state?.back)
});

function hasAcceptedImage(rating) {
  return isSteelDamageRating(rating) && acceptedImage.value;
}

const statusOptions = [
  { label: I18n.t('issues.open_issue'), value: 'open_issue' },
  { label: I18n.t('issues.resolved'), value: 'resolved' },
];

const elementMaterialOptions = [
  'steel',
  'concrete',
  'fibreglass',
  'slope',
  'asbestos',
  'timber',
].map((value) => ({
  label: I18n.t(`element_materials.${value}`),
  value,
}));

const elementClassOptions = [
  'primary',
  'secondary',
  'tertiary',
  'non_structural',
].map((value) => ({
  label: I18n.t(`element_classes.${value}.name`),
  value,
  description: [I18n.t(`element_classes.${value}.description`)],
  disabled: false,
}));

const overallConditionOptions = DEFAULT_RATINGS.map((rating) => ({
  label: rating.toString(),
  value: rating,
  description: I18n.t(`condition_descriptions.${rating}`),
  iconClass: `issue-ratings__indicator issue-ratings__indicator--condition-${rating}`,
}));


function priorityLabel(priority) {
  if (currentOrganisation.value?.aga_profile) {
    return I18n.t(`aga_priority_labels.${priority}`);
  } else {
    return I18n.t(`priority_labels.${priority}`);
  }
}

const repairPriorityOptions = computed(() => ['none', 'M', '3', '2', '1', 'X'].map(
  (priority) => ({
    label: priority === 'none' ? '-' : priorityLabel(priority),
    value: priority,
    description: I18n.t(`priority_descriptions.${priority}`),
    iconClass: `issue-ratings__indicator issue-ratings__indicator--priority-${priority}`,
  })
));

const deteriorationMechanisms = computed(() => {
  if (!activity.element_material && isShaftV2.value) return {};

  let dms = getAllDeteriorationMechanisms();
  if (!dms) return {};

  activity.deterioration_mechanism_ratings.forEach((r) => delete dms[r.name]);

  return dms;
});

const locationDescription = computed(() => {
  if (!location.value) return;

  switch (location.value.type) {
    case 'shaft':
      return [
        I18n.t(`shafts.element_types.shaft.${location.value?.elementType}`),
        location.value?.element?.name,
        `${I18n.t('shafts.issues.set')} ${location.value?.set}`,
      ].join(', ');
    case 'station':
      return [
        location.value?.station?.name,
        location.value?.stationMember?.name,
      ].join(', ');
    case 'heavy_mobile_equipment':
      return [
        location.value?.heavy_mobile_equipment?.name,
        location.value?.heavyMobileEquipmentMember?.name,
      ].join(', ');
    default:
      return null;
  }
});

/**
 * Disable the location button if the location we are editing
 * is Hme and heavy_mobile_equipment_enabled is false
 */
const disableLocation = computed(() => {
  const isHme = location.value?.type === 'heavy_mobile_equipment';
  const disableStatus = activity?.id && isHme && !currentShaft.value?.policy?.heavy_mobile_equipment_enabled;
  return disableStatus;
});

onMounted(async () => {
  await loadInitialIssue();
  if (isShaftV2.value && location.value) {
    shaftIssueLocationStore.$patch({
      location: location.value
    });
  }
});

function formatLocation(locationData) {
  if (locationData?.set_number) {
    const locationElementData = {
      set_id: locationData.id,
      position: locationData.shaft_element.position,
      name: locationData.shaft_element.name
    };

    return {
      type: 'shaft',
      description: 'shaft',
      elementType: locationData.shaft_element.element_type,
      set: locationData?.set_number,
      element: locationElementData
    };
  } else {
    let operationData = null;
    switch(true) {
      case !!locationData?.station_id:
        operationData = {
          name: 'station',
          id_text: 'station_id',
          member_text: 'stationMember'
        };
        break;
      case !!locationData?.heavy_mobile_equipment_id:
        operationData = {
          name: 'heavy_mobile_equipment',
          id_text: 'heavy_mobile_equipment_id',
          member_text: 'heavyMobileEquipmentMember'
        };
        break;
    }

    if (!operationData) return;

    const locationOperationData = {
      location: locationData.location,
      [operationData.id_text]: locationData[operationData.id_text] || '',
      direct_region_id: locationData.direct_region_id,
      region: null,
      name: locationData.name
    };

    return {
      type: operationData.name,
      description: operationData.name,
      [operationData.member_text]: '',
      diagram_identifier: locationData.diagram_identifier,
      [operationData.name]: locationOperationData
    };
  }
}

onBeforeUnmount(() => {
  destroyCarousel();
  clearAllBodyScrollLocks();
});

watch(() => props.issueId, loadInitialIssue);

watch(
  () => activity,
  (newValue) => {
    if (isShaftV2.value) {
      shaftIssueLocationStore.$patch({ formData: newValue });
    }
  }, { deep: true }
);

watchEffect(() => viewingImage.value ? disableBodyScroll() : enableBodyScroll());

const canEdit = computed(() => {
  if (props.issueId) return initialActivity.value?.policy?.edit || false;
  return true;
});

const downloadIssuePath = computed(() => {
  return activity.download_url;
});

/**
 * Update the currently selected image details in the issue_images
 * array on the current issue
 */
function updateCurrentImage(image) {
  const index = findSelectedImageIndex(image);

  if (image?.id) {
    activity.detail.issue_images[index].description = image?.description;
  }
}

/**
 * If an image has been saved, use the blob id to find an image
 * in the issue images. This is because new images have dummy id's.
 * When an issue has not yet been saved, the dummy id's are fine to use.
 */
function findSelectedImageIndex(selectedImage) {
  return activity.detail.issue_images.findIndex((image) => {
    if (image['issue_id']) {
      return image['image_blob']['id'] === selectedImage['image_blob']['id'];
    } else {
      return image['id'] === selectedImage['id'];
    }
  });
}

/**
 * Update the currently selected image annotation details in the issue_images
 * array on the current issue
 */
function annotateImage(image) {
  const index = findAnnotatedImageIndex(image);

  activity.detail.issue_images[index].isNew = image?.isNew;
  activity.detail.issue_images[index].description = image?.description;
  activity.detail.issue_images[index].image_blob = image?.image_blob;
  activity.detail.issue_images[index].original_image_blob ??= image?.original_image_blob;
  activity.detail.issue_images[index].upload = image?.upload;
  activity.detail.issue_images[index].original_upload ??= image?.original_upload;
  activity.detail.issue_images[index].original_url ??= image?.original_url;
  activity.detail.issue_images[index].url = image?.url;
  activity.detail.issue_images[index].markup = image?.markup;
  updateCarousel();
}

/**
 * All annotated images should have id's at the point this is required
 */
function findAnnotatedImageIndex(selectedImage) {
  return activity.detail.issue_images.findIndex((image) => {
    return image['id'] === selectedImage['id'];
  });
}


let imageCarousel = null;
function initCarousel() {
  imageCarousel = new IssueCarousel(
    imagesContainer.value,
    shaftData.value.issueUrl,
    sortedImages.value,
    image => {
      router.replace({ query: { tab: 'description' } });
      viewingImage.value = image
    },
  );
}

function destroyCarousel() {
  if (imageCarousel) {
    imageCarousel.destroy();
  }
}

function updateCarousel(...args) {
  imageCarousel.update(sortedImages.value, ...args);
}

const loadInspection = async () => {
  let { ok, data } = await ajax(`${shaftData.value.inspectionUrl}`);

  if (ok) {
    initialInspectionDate.value = data.date;
  }
};

async function loadShaftLocation(location) {
  let { ok, data } = await ajax(`${shaftData.value.shaftLocationUrl}/${location?.shaft_element_id}`);

  if (ok) {
    return {
      type: 'shaft',
      elementType: data.element_type,
      element: { ...data, group_name: data.shaft_element_group.name },
      set: location.set_number
    };
  }
}

async function loadStationLocation(location) {
  let { ok, data } = await ajax(`${shaftData.value.stationLocationUrl}/${location.station_id}`);

  if (ok) {
    let stationMember = data
      .station_template
      .station_template_members
      .find(({ id }) => id === location.station_template_member_id);
    return {
      type: 'station',
      elementType: stationMember.element_type,
      station: data,
      stationMember,
    };
  }
}

async function loadLocation(member) {
  switch (member?.location_type) {
    case 'Shafts::ShaftLocation':
      return await loadShaftLocation(member.location);
    case 'Shafts::StationLocation':
      return await loadStationLocation(member.location);
  }
}

async function loadInitialIssue() {
  await loadInitialActivity();
  await nextTick();
  initCarousel();
}

async function loadIssue(data) {
  issueStore.updateIssue(data);
  lastGoodActivity = prepareActivity(data);
  Object.assign(activity, lastGoodActivity);
  activity.element_material = lastGoodActivity.element_material;
  isShaftV2.value && loadSetElements(data.location.set_number, data.location.shaft_element_set_id);
  !isShaftV2.value && (location.value = await loadLocation(data.member));

  !isShaftV2.value && await fetchExistingDetMechs();
}

async function loadInitialActivity() {
  loading.value = true;

  if (isShaftV2.value && shaftIssueLocationStore.formData) {
    Object.assign(activity, shaftIssueLocationStore.formData);
  }

  if (isShaftV2.value && shaftIssueLocationStore.location) {
    location.value = shaftIssueLocationStore.location;
    activity.location_attributes = shaftIssueLocationStore.location;
  }

  if (props.issueId) {
    initialActivity.value = null;
    let { ok, data } = await ajax(shaftData.value.issueUrl);

    if (ok) {
      initialActivity.value = prepareActivity(data);
      lastGoodActivity = prepareActivity(data);
      Object.assign(activity, lastGoodActivity);
      activity.element_material = lastGoodActivity.element_material;
      if(isShaftV2.value) {
        let operation = {};
        switch (data.location_type) {
          case 'ShaftsV2::ShaftLocation':
            await loadSetElements(data.location.set_number, data.location.shaft_element_set_id, data.location.position);
            break;
          case 'ShaftsV2::StationLocation':
            operation['url'] = shaftData.value.stationLocationUrl;
            operation['value'] = await loadOperation(operation.url, data.location.station_id);
            location.value = formatLocation({...data.location, ...operation.value});
            break;
          case 'ShaftsV2::HeavyMobileEquipmentLocation':
            operation['url'] = shaftData.value.hmeLocationUrl;
            operation['value'] = await loadOperation(operation.url, data.location.heavy_mobile_equipment_id);
            location.value = formatLocation({...data.location, ...operation.value});
            break;
          default:
            return null;
        }
      }
      !isShaftV2.value && (location.value = await loadLocation(data.member));
      !isShaftV2.value && await fetchExistingDetMechs();
    }
  } else {
    await loadInspection();
    initialActivity.value = createNewActivity(initialInspectionDate.value);
  }

  loading.value = false;
}

async function loadSetElements(setNumber, setId, position) {
  let { ok, data } = await ajax(`${shaftData.value.setElements}/${setNumber}`);

  if (ok) {
    const currentElementData = data?.shaft_element_set.find(element => element?.id === setId);
    if (!currentElementData) return;
    if (position) {
      currentElementData.shaft_element.position = position;
    }
    location.value = formatLocation(currentElementData);
  }
}

async function loadOperation(operationUrl, operationId) {
  if (operationId) {
    loadingOperation.value = true;
    let { ok, data } = await ajax(`${operationUrl}/${operationId}`);

    if (ok) {
      loadingOperation.value = false;
      return data;
    }
  }
}

async function loadOperationRegions(operationUrl, operationId, regionEndpoint) {
  if (operationId) {
    loadingOperationRegion.value = true;
    let { ok, data } = await ajax(`${operationUrl}/${operationId}/${regionEndpoint}`);

    if (ok) {
      loadingOperationRegion.value = false;
      return data.regions;
    }
  }
}

function createNewActivity(inspectionDate) {
  const shaftV2Activity = route?.path?.includes('shafts_v2') ? {
    title: '',
    status: '',
    description: '',
    recommendation: '',
    location_description: '',
    priority: null,
    element_material: null,
    element_class: null,
    requires_task: false,
    tasks_close_issue: false,
    location_type: null,
    location_attributes: {},
    tasks: [],
    download_url: null,
    policy: { create: true }
  } : {};

  return {
    detail: {
      description: '',
      issue_images: [],
      issue_documents: []
    },
    overall_condition_rating: null,
    deterioration_mechanism_ratings: [],
    inspection: { date: inspectionDate },
    ...shaftV2Activity
  };
}

function prepareActivity(data) {
  const shaftV2Activity = route.path.includes('shafts_v2') ? {
    detail: {
      description: data?.description,
      issue_images: data?.issue_images,
      issue_documents: data?.issue_documents,
      inspection_id: data?.inspection_id,
      inspection: {
        ...data?.inspection,
        ...data?.inspected_by,
        ...data?.risk_rating,
        ...data?.created_at,
        ...data?.updated_at,
        ...data?.shaft_id,
      }
    },
  } : {};
  return {...data , ...shaftV2Activity};
}

async function formatImageForPost(image) {
  if (image.isNew) {
    // means image was already uploaded, now needs original uploaded
    if (image?.original_upload) {
      await uploadImage(image, 'original_image');
    }
    await uploadImage(image);
    return {
      image: image.image_blob.signed_id,
      original_image: image?.original_image_blob?.signed_id,
      description: image.description,
      markup: image?.markup,
      damage_marker_markup: image?.damage_marker_markup,
      calculation_type: image.calculation_type,
      damaged_structure: image.damaged_structure,
      thickness: image?.thickness,
      thickness_unit: image?.thickness_unit,
      deviation: image?.deviation,
      deviation_unit: image?.deviation_unit,
      accepted: image?.accepted,
    };
  }
}

function formatDMRForPost(dmr) {
  return sliceObject(dmr, ['name', 'value']);
}

function formatMemberForPost() {
  if (!location.value) return {};

  switch (location.value.type) {
    case 'shaft':
      if (isShaftV2.value) return {
        location_type: 'ShaftsV2::ShaftLocation',
        location_attributes: {
          set_number: location.value.set,
          shaft_element_set_id: location.value.element.set_id,
          position: location.value.element.position,
        }
      };
      return {
        location_type: 'Shafts::ShaftLocation',
        location_attributes: {
          set_number: location.value.set,
          shaft_element_id: location.value.element.id,
        }
      };
    case 'station':
      if (isShaftV2.value) return {
        location_type: 'ShaftsV2::StationLocation',
        location_attributes: {
          location: location.value.station.location,
          station_id: location.value.station.station_id,
          direct_region_id: location.value.station.direct_region_id,
        }
      };
      return {
        location_type: 'Shafts::StationLocation',
        location_attributes: {
          station_id: location.value.station.id,
          station_template_member_id: location.value.stationMember.id
        }
      };
    case 'heavy_mobile_equipment':
      if (isShaftV2.value) return {
        location_type: 'ShaftsV2::HeavyMobileEquipmentLocation',
        location_attributes: {
          location: location.value.heavy_mobile_equipment.location,
          heavy_mobile_equipment_id: location.value.heavy_mobile_equipment.heavy_mobile_equipment_id,
          direct_region_id: location.value.heavy_mobile_equipment.direct_region_id,
        }
      };
  }
}

const formatActivityForPost = async () => {
  return {
    ...sliceObject(activity, [
      'overall_condition_rating',
    ]),
    detail_attributes: {
      description: activity.detail.description,
      issue_images_attributes: await Promise.all(
        activity.detail.issue_images.map(formatImageForPost)
      ),
      issue_documents_attributes: await Promise.all(
        activity.detail.issue_documents.map(formatDocumentForPost)
      )
    },
    deterioration_mechanism_ratings_attributes: activity.deterioration_mechanism_ratings.map(
      formatDMRForPost
    ),
    member_attributes: formatMemberForPost()
  };
};

const formatShaftV2ActivityForPost = async () => {
  const locationData = formatMemberForPost();

  return {
    ...sliceObject(activity, [
      'title',
      'overall_condition_rating',
      'recommendation',
      'priority',
      'element_material',
      'element_class',
      'requires_task',
    ]),
    status: activity.status ? activity.status : 'open_issue',
    description: activity.description,
    tasks_close_issue: activity.tasks_close_issue,
    location_description: locationData?.description,
    location_type: locationData?.location_type,
    location_attributes: locationData?.location_attributes,
    issue_images_attributes: await Promise.all(
      activity.detail.issue_images.map(formatImageForPost)
    ),
    issue_documents_attributes: await Promise.all(
      activity.detail.issue_documents.map(formatDocumentForPost)
    ),
    deterioration_mechanism_ratings_attributes: activity.deterioration_mechanism_ratings.map(
      formatDMRForPost
    ),
    tasks_attributes: await Promise.all(activity.tasks.map(formatTaskForPost)),
  };
};

async function saveNewActivity() {
  saving.value = true;
  const newActivity = isShaftV2.value ? await formatShaftV2ActivityForPost() : { activity: await formatActivityForPost() };
  let { ok, data } = await ajax(`${shaftData.value.inspectionUrl}/issues`, {
    method: 'POST',
    body: newActivity,
  });

  if (ok) {
    errors.value = null;
    if (shaftIssueLocationStore.saveAndCreateNewIssue) {
      Object.assign(activity, createNewActivity());
      updateCarousel();
      emitter.emit('toast', { status: 'success', message: I18n.t('issues.new_issue_success_toast') });
      location.value = null;
      shaftIssueLocationStore.location = null;
      shaftIssueLocationStore.formData = null;
      await nextTick();
      issueEditFieldInputs.forEach(input => input.value && (input.value.internalValue = ''));
    } else {
      shaftIssueLocationStore.$reset();
      router.replace({
        name: shaftData.value.inspection,
        params: { scope: 'inspections' }
      });
    }
  } else {
    errors.value = data;
  }

  saving.value = false;
}

const hasError = (field) => {
  return errors.value && (
    !!(errors.value[field]) ||
    !!(errors.value.activity && errors.value.activity[field]) ||
    !!(errors.value.issue && errors.value.issue[field])
  );
};

const validateInput = (field) => {
  if (errors.value) {
    if (!isShaftV2.value && field === 'overall_condition_rating' || field === 'location_type') return (errors.value = null);
    if (!isShaftV2.value) return errors.value?.activity[field] && (errors.value.activity[field] = null);
    errors.value?.issue[field] && (errors.value.issue[field] = null);
  }
};

async function formatDocumentForPost(file) {
  if (file.isNew) {
    await uploadDocument(file);
    return { file: file.file_blob.signed_id };
  }
}

/**
 * Returns the correct text and action for the dialog.
 */
const dialogData = computed(() => {
  switch (currentDialogType.value) {
    case 'image':
      return {
        text: dialogTextObject('image'),
        action: removeImage,
      };
    case 'issue':
      return {
        text: dialogTextObject('issue'),
        action: deleteIssue,
      };
    default:
      return {
        text: dialogTextObject('task'),
        action: deleteTask,
      };
  }
});

/**
 * Toggles dialog,
 * stores the id used for deletion,
 * stores the type of dialog. eg. image, file, task
 */
function setDialogTypeAndId(type, id) {
  dialogIsOpen.value = true;
  deletionId.value = id;
  currentDialogType.value = type;
}

/**
 * Toggles dialog for removing an image,
 */
function confirmDeleteDialog(type, id) {
  setDialogTypeAndId(type, id);
}

/**
 * Toggles dialog for removing a file.
 */
function fileDeleteDialog(type, id, { key }) {
  if (key === 'delete') {
    setDialogTypeAndId(type, id);
  }
}

/**
 * Sets the correct text for the dialog message based off the type.
 */
function dialogTextObject(type) {
  return {
    title: I18n.t('issues.confirm_delete_title', { type: I18n.t(`issues.type_${type}`) }),
    message: type === 'issue' ? I18n.t('issues.confirm_issue_delete_message') : I18n.t('issues.confirm_delete_message'),
  };
}

/**
 * Closes and clears any stored values
 */
function dismissDialogAction() {
  dialogIsOpen.value = false;
  deletionId.value = null;
}

async function uploadImage(image, type = 'image') {
  const upload = type !== 'original_image' ? image.upload : image.original_upload;
  const blob = await uploader.upload(upload);
  if (type === 'image') {
    Object.assign(image.image_blob, blob);
  } else if (type === 'original_image') {
    Object.assign(image.original_image_blob, blob);
  }
  return blob;
}

async function uploadDocument(document) {
  let blob = await uploader.upload(document.upload);
  Object.assign(document.file_blob, blob);
  return blob;
}

async function removeImage() {
  if (!props.issueId) {
    activity.detail.issue_images.splice(activity.detail.issue_images.findIndex((image) => {
      return image.id === deletionId.value;
    }), 1);
  } else {
    const imageObject = { issue_images_attributes: [{ id: deletionId.value, _destroy: true }] };
    const updatedBody = isShaftV2.value ? { issue: imageObject } : { activity: { detail_attributes: imageObject } };

    let { ok } = await ajax(shaftData.value.issueUrl, {
      method: 'PATCH',
      body: updatedBody
    });

    if (ok) {
      activity.detail.issue_images = sortedImages.value.filter(img => img.id !== deletionId.value);
      imageValid.value = true;

      issueStore.deleteImage(activity.id, deletionId.value);
    }
  }

  updateCarousel();
  dismissDialogAction();
}

function openDamageCalculation(image_id) {
  router.replace({ query: { tab: 'damage_calculator' } });
  viewingImage.value = activity.detail.issue_images.find((image) => image.id === image_id);
}

async function removeDocument(id) {
  if (props.issueId) {
    const documentObject = { issue_documents_attributes: [{ id: id, _destroy: true }] };
    const updatedBody = isShaftV2.value ? { issue: documentObject } : { activity: { detail_attributes: documentObject } };
    let { ok } = await ajax(shaftData.value.issueUrl, {
      method: 'PATCH',
      body: updatedBody
    });

    if (!ok) return;
  }
  activity.detail.issue_documents = activity.detail.issue_documents.filter(doc => doc.id !== id);

  dismissDialogAction();
}

async function deleteIssue() {
  let { ok } = await ajax(shaftData.value.issueUrl, { method: 'DELETE' });

  if (ok) {
    router.back();
    dismissDialogAction();
  }
}

function updateFileList(file, type) {
  if (type === 'image') {
    activity.detail.issue_images.push(file);
    updateCarousel(sortedImages.value.length - 1);
  } else {
    activity.detail.issue_documents.push(file);
  }
}

async function saveBlob(file, type) {
  if (!props.issueId) return;
  const blobData = await uploadFile(file, type);
  const fileObject = type === 'image' ?
  { issue_images_attributes: [{ image: blobData.signed_id }] } :
  { issue_documents_attributes: [{ file: blobData.signed_id }] };
  const updatedBody = isShaftV2.value ? fileObject : { detail_attributes: fileObject };
  updateActivity(updatedBody);
}

function wrongFileType(imageData) {
  const isEmpty = Object.keys(imageData).length <= 0;
  imageValid.value = isEmpty;
  imageValidationMessage.value = !isEmpty ? I18n.t('general.image_validation_message', imageData) : null;
}

async function uploadFile(file, type) {
  const upload = type !== 'original_image' ? file.upload : file.original_upload;
  const blob = await uploader.upload(upload);
  if (type === 'image') {
    Object.assign(file.image_blob, blob);
  } else if (type === 'original_image') {
    Object.assign(file.original_image_blob, blob);
  } else if (type === 'attachment') {
    Object.assign(file.file_blob, blob);
  }
  return blob;
}

function getAllDeteriorationMechanisms() {
  switch (location.value?.type) {
    case 'shaft':
      if(isShaftV2.value) return { ...DETERIORATION_MECHANISMS[activity.element_material] };
      if (location.value.elementType) {
        return { ...SHAFT_DETERIORATION_MECHANISMS[location.value.elementType] };
      }
      break;
    case 'station':
    case 'heavy_mobile_equipment':
      if(isShaftV2.value) return { ...DETERIORATION_MECHANISMS[activity.element_material] };
      if (location.value.elementType) {
        return { ...STATION_DETERIORATION_MECHANISMS[location.value.elementType] };
      }
      break;
  }
  return {};
}

async function applyDamageRating(damageRating, values) {
  if (activity.element_material && activity.element_material !== 'steel') {
    return;
  }

  activity.element_material ||= 'steel';

  const index = findSelectedImageIndex(viewingImage.value);
  const structuralDamage = activity.deterioration_mechanism_ratings.find((rating) => {
    return rating['name'] === 'structural_damage';
  });

  clearAcceptedImageLocally();

  if (props.issueId) {
    const imageAttributes = clearedAcceptedImageAttributes();
    const acceptedIndex = imageAttributes.findIndex((image) => image.id === viewingImage.value.id);
    imageAttributes[acceptedIndex] = {
      id: viewingImage.value.id,
      ...values,
      accepted: true
    };
    await updateActivity({
      issue_images_attributes: imageAttributes
    });
  } else {
    activity.detail.issue_images[index] = {
      ...activity.detail.issue_images[index],
      ...values,
      accepted: true,
    };
  }

  if (structuralDamage) {
    await updateRating(structuralDamage, damageRating, false);
  } else {
    createNewRating('structural_damage', deteriorationMechanisms.value[damageRating]);
    await addRating(damageRating);
  }
}

async function clearAcceptedImage() {
  clearAcceptedImageLocally();

  if (props.issueId) {
    await updateIssue({
      issue_images_attributes: clearedAcceptedImageAttributes()
    });
  }
}

function currentAcceptedImageIndex() {
  return activity.detail.issue_images.findIndex((image) => {
    return image.accepted === true;
  });
}

function clearAcceptedImageLocally() {
  const index = currentAcceptedImageIndex();

  if (index < 0) return;

  activity.detail.issue_images[index].accepted = false;
}

function clearedAcceptedImageAttributes() {
  return activity.detail.issue_images.map((image) => {
    return {
      id: image.id,
      accepted: false,
    };
  });
}

async function clearDamageRating() {
  const index = findSelectedImageIndex(viewingImage.value);

  const valuesToClear = {
    calculation_type: null,
    damage_marker_markup: {},
    damaged_structure: null,
    deviation: null,
    deviation_unit: null,
    thickness: null,
    thickness_unit: null,
    accepted: null,
  }

  if (props.issueId) {
    await updateImage(activity.detail.issue_images[index], {
      ...valuesToClear,
    });
  } else {
    activity.detail.issue_images[index] = {
      ...activity.detail.issue_images[index],
      ...valuesToClear,
    };
  }
}

async function updateImage(image, value) {
  await updateActivity({
    issue_images_attributes: [{ id: image.id, ...value }],
  });
}

function createNewRating(name, detMech) {
  newRating.value = { name, ...detMech };
  window.dispatchEvent(new Event('resize')); // HACK: force popper to reposition
}

function cancelNewRating() {
  newRating.value = null;
  newRatingOpen.value = false;
}

function addRating(rating) {
  activity.deterioration_mechanism_ratings.push({
    id: uuid(),
    name: newRating.value.name,
    value: rating,
  });
  updateConditionRating();
  updateActivity({
    deterioration_mechanism_ratings_attributes: [{ name: newRating.value.name, value: rating }],
  });

  newRating.value = null;
  newRatingOpen.value = false;
}

function ratingItems(detMech) {
  const currentIssueName = `${issueName.value?.name ? issueName.value?.name : detMech?.name}`;
  issueName.value = '';
  let enabledRatings = getAllDeteriorationMechanisms()[detMech.name]?.ratings || DEFAULT_RATINGS;

  const detMechText = isShaftV2.value ?
    `dmr_descriptions.${activity.element_material}.${currentIssueName}` :
    `shafts.dmr_descriptions.${location.value.elementType}.${currentIssueName}`;

  return DEFAULT_RATINGS.map((rating) => ({
    value: rating,
    label: rating.toString(),
    description: I18n.t(`${detMechText}.${rating}`),
    disabled: !enabledRatings.includes(rating) || rating < (existingDetMechs.value[detMech.name] || 0),
    iconClass: `issue-ratings__indicator issue-ratings__indicator--condition-${rating}`,
  }));
}

async function updateRating(rating, value, clearAcceptedImages = true) {
  rating.value = value;
  updateConditionRating();

  let updates = {
    deterioration_mechanism_ratings_attributes: [{ id: rating.id, value }],
  };

  if (isSteelDamageRating(rating) && clearAcceptedImages && currentAcceptedImageIndex() >= 0) {
    clearAcceptedImageLocally();
    updates['issue_images_attributes'] = clearedAcceptedImageAttributes();
  }

  await updateActivity(updates);
}

const updateDescription = (item) => {
  issueName.value = item;
};

async function removeRating(rating) {
  activity.deterioration_mechanism_ratings = activity.deterioration_mechanism_ratings.filter(
    (x) => x.id !== rating.id
  );
  updateConditionRating();

  let updates = {
    deterioration_mechanism_ratings_attributes: [
      { id: rating.id, _destroy: true },
    ],
  };

  if (isSteelDamageRating(rating) && currentAcceptedImageIndex() >= 0) {
    clearAcceptedImageLocally();
    updates['issue_images_attributes'] = clearedAcceptedImageAttributes();
  }

  await updateActivity(updates);
}

function isSteelDamageRating(rating) {
  return activity?.element_material === 'steel' && rating.name === 'structural_damage';
}

function updateConditionRating() {
  let allRatings = activity.deterioration_mechanism_ratings
    .map(x => x.value);
  activity.overall_condition_rating = Math.max(existingRating.value, ...allRatings);
  updatePriority();
}

function updateLocation() {
  activity.deterioration_mechanism_ratings = [];
  fetchExistingDetMechs().then(updateConditionRating);
  updateActivity({
    member_attributes: formatMemberForPost(),
  });
  validateInput('location_type');
}

async function updateActivity(attributes) {
  if (!props.issueId) return;
  const isTask = !!attributes?.tasks_attributes;
  // Reset the previous possible error that might have occurred for the buttons to stop loading
  errorSavingTask.value = null;
  toggleIssueModal.value = null;

  const updatedBody = isShaftV2.value ? { issue: attributes } : { activity: attributes };

  let { ok, data } = await ajax(shaftData.value.issueUrl, {
    method: 'PATCH',
    body: updatedBody,
  });

  if (ok) {
    // Closes the task modal when saving is complete
    if (isTask) {
      newTask.value = null;
      toggleIssueModal.value = false;
    }

    loadIssue(data);
  } else {
    Object.assign(activity, lastGoodActivity);
    activity.element_material = lastGoodActivity.element_material;
    isShaftV2.value && loadSetElements(lastGoodActivity.location.set_number, lastGoodActivity.location.shaft_element_set_id);
    !isShaftV2.value && (location.value = await loadLocation(lastGoodActivity.member));
    // Notifies the task modal that an error occurred and the buttons should be enabled
    isTask && (errorSavingTask.value = true);
    errors.value = data;
  }

}

async function updateElementMaterial(newMaterial) {
  activity.element_material = newMaterial;
  activity.deterioration_mechanism_ratings = [];
  activity.overall_condition_rating = null;
  updatePriority();
  await updateActivity({ element_material: newMaterial });

  validateInput('element_material');
}

async function update(field, value) {
  activity[field] = value;
  if (field === 'overall_condition_rating' || (field === 'element_class' && isAga.value)) updatePriority();
  await updateActivity({ [field]: value });

  validateInput(field);
}

function updatePriority() {
  activity.priority = ratingToPriority(activity.overall_condition_rating, { elementClass: activity.element_class, isAga: isAga.value });
  validateInput('overall_condition_rating');
  isShaftV2.value && validateInput('priority');
}

/** Tasks start */
function createNewTask() {
  newTask.value = {
    isNew: true,
    id: uuid(),
    labels: [],
    status: 'incomplete',
  };
}

async function formatTaskForPost(task) {
  return {
    ...sliceObject(task, [
      'description',
      'external_reference',
      'task_type',
      'status',
      'labels',
    ]),
    due_date: isoDate(task.due_date),
    task_documents_attributes: await Promise.all(
      task.task_documents.map(formatDocumentForPost)
    ),
  };
}

async function addNewTask(task) {
  activity.tasks = [...activity.tasks, task];
  if (props.issueId) {
    updateActivity({
      tasks_attributes: [
        {
          ...(await formatTaskForPost(task)),
          status: undefined,
        },
      ],
    });
  } else {
    errorSavingTask.value = false;
    newTask.value = null;
    toggleIssueModal.value = false;
  }
}

async function updateTask(task, updatedTask) {
  // check if any docs removed
  const deletedDocuments = task.task_documents.filter((id) => {
    return !updatedTask.task_documents.includes(id);
  });

  // remove docs
  if (deletedDocuments) {
    deletedDocuments.forEach((document) => {
      updateActivity({
        tasks_attributes: [
          { id: task.id, task_documents_attributes: [{ id: document.id, _destroy: true }] },
        ],
      });
    });
  }

  Object.assign(task, updatedTask);
  if (props.issueId) {
    updateActivity({
      tasks_attributes: [
        { id: task.id, ...(await formatTaskForPost(task)), status: undefined },
      ],
    });
  }
}

function markTaskAs(task, status) {
  task.status = status;
  if (status !== 'incomplete') {
    task.closed_at = new Date();
    task.closed_by = currentUser;
  }
  if (props.issueId) {
    updateActivity({ tasks_attributes: [{ id: task.id, status }] });
  }
}

function deleteTask() {
  activity.tasks = activity.tasks.filter((x) => x.id !== deletionId.value);
  if (props.issueId) {
    updateActivity({ tasks_attributes: [{ id: deletionId.value, _destroy: true }] });
  }

  dismissDialogAction();
}
/** Tasks end */

async function fetchExistingDetMechs() {
  if (!location.value) return;

  let path = `/organisations/${props.organisationId}/shafts/${props.shaftId}`;
  switch (location.value.type) {
    case 'shaft':
      path += `/shaft_elements/${location.value.element.id}/members/${location.value.set}`;
      break;
    case 'station':
      path += `/stations/${location.value.station.id}/members/${location.value.stationMember.id}`;
      break;
  }

  let { ok, data } = await ajax(path);

  if (ok) {
    existingDetMechs.value = data.deterioration_mechanism_ratings;
    existingRating.value = data.overall_condition_rating;
  }
}

// STATION AND HME STUFF
async function onMapInit(locationData) {
  let operation = {};
  operation['type'] = location.value?.type;
  switch (location.value?.type) {
    case 'station':
      operation['url'] = shaftData.value.stationLocationUrl;
      operation['id'] = locationData.station.station_id;
      operation['endpoint'] = shaftData.value.stationRegions;
      break;
    case 'heavy_mobile_equipment':
      operation['url'] = shaftData.value.hmeLocationUrl;
      operation['id'] = locationData.heavy_mobile_equipment.heavy_mobile_equipment_id;
      operation['endpoint'] = shaftData.value.hmeRegions;
      break;
  }
  if (
    !operation?.url ||
    !operation?.id ||
    !operation?.endpoint ||
    (location.value && !location.value[operation.type])
  ) return;

  operationRegions.value = await loadOperationRegions(operation.url, operation.id, operation.endpoint);
  const region = operationRegions.value?.find(region => region.id === location.value[operation.type]?.direct_region_id);
  location.value[operation.type]['region'] = region?.name || null;
  if (operationRegions.value) {
    operationRegions.value.forEach(loadRegion);
  }

  if (locationData[operation.type]?.location) {
    let location = rgeoToLatLng(locationData[operation.type].location);
    setMarker(...location);
    map.value?.goTo(location, 18);
  } else if (!screenAtLeast('medium')) {
    let { lat, lng } = map.value?.getCenter();
    setMarker(lat, lng);
  }
}

function loadRegion(region) {
  if (!region || !map.value) return;
  let geoJSON = rgeoPolygonToGeoJSON(region.bounds);
  let latLngs = geoJSONPolygonToLatLngs(geoJSON);
  let polygon = map.value.addPolygon(
    latLngs,
    regionPolygonOptions(region.region_type)
  );
  let regionObject = {
    ...region,
    geoJSON,
    latLngs,
    polygon,
  };
  polygon.region = regionObject;
}

function setMarker(lat, lng) {
  if (!map.value) return;
  map.value.enableCrosshair(lat, lng);
  if (!marker) {
    marker = map.value.addMarker([lat, lng], { icon: new PointIcon() });
  } else {
    marker.setLatLng([lat, lng]);
  }
}

/**
 * Check if the back route is of inspections,
 * if it is we want to use the route instead of
 * the router.back() method to avoid a loop in
 * navigation between the location page and the issue form
*/
function useRouterPath(back) {
  if (!back) return true;
  return back.includes('/inspections/');
}
</script>
<style scoped lang="scss">
@import '@/stylesheets/global';

.error {
  color: map-get($error-color, default);
}

.disabled {
  cursor: not-allowed;
}
</style>
